<script module>
  import { gql } from '$lib/graphql.svelte.js'

  export const i18n = {
    en: {
      YOUTH: 'youth',
      SENIOR: 'senior',
      ADULT: 'adult',
      '%s ADULT': {
        one: '%s adult',
        other: '%s adults'
      },
      '%s SENIORR': {
        one: '%s senior',
        other: '%s seniors'
      },
      '%s YOUTH': {
        one: '%s youth',
        other: '%s youths'
      },
      '%s people': {
        one: '%s person',
        other: '%s people'
      },
      '%s nights in': {
        one: 'One night in',
        other: '%s nights in'
      },
      '%s changes': {
        one: '%s change',
        other: '%s changes'
      },
      '%s days': {
        one: '%s day',
        other: '%s days'
      }
    },
    sv: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'vuxen',
      '%s ADULT': {
        one: '%s vuxen',
        other: '%s vuxna'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdomar'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      '%s nights in': {
        one: 'En natt i',
        other: '%s nätter i'
      },
      '%s changes': {
        one: '%s byte',
        other: '%s byten'
      },
      '%s days': {
        one: '%s dag',
        other: '%s dagar'
      },
      'It is too close to your selected departure date, try a later date.':
        'Det är för nära din valda avgångsdatum, försök med ett senare datum.',
      'There was a problem connecting to the booking system for this route. Please try again later.':
        'Det uppstod ett problem i vår koppling till bokningssystemet för denna resa. Försök igen senare.',
      'Tickets between %s and %s are typically purchased at the station.':
        'Biljetter mellan %s och %s köps vanligtvis på stationen.',
      'These tickets are typically purchased at the station.':
        'Dessa biljetter köps vanligtvis på stationen.',
      'Tickets for the journey between %s and %s are sold out.':
        'Biljetter för resan mellan %s och %s är slutsålda.',
      'Tickets for the %s departure from %s to %s are sold out.':
        'Biljetter för avgången vid %s från %s till %s är slutsålda.',
      "We're currently unable to offer a ticket for this route. Please check with the operator directly.":
        'Vi kan för tillfället inte erbjuda biljetter för denna resa. Kontakta operatören direkt.',
      'Tickets for your journey are not yet available. Please try again at a later stage.':
        'Biljetterna för din resa är inte tillgängliga än. Försök igen senare.',
      "Sorry, our booking system didn't return any results for this route.":
        'Vi kunde inte hitta några biljetter för denna resa.',
      'The timetable for this route is not yet available. Please try again at a later stage.':
        'Tidtabellen för denna resa är inte tillgänglig än. Försök igen senare.',
      "It's too early to book": 'För tidigt att boka',
      'No tickets found': 'Inga biljetter hittade',
      'Timetable not available': 'Tidtabell ej tillgänglig',
      'Tickets unavailable': 'Ej tillgängligt',
      'Check with operator': 'Kontakta operatören',
      'Tickets are sold out': 'Biljetterna är slutsålda',
      'Prebook not possible': 'Går ej att boka',
      'Show details': 'Visa detaljer',
      'Hide details': 'Dölj detaljer',
      'Show price': 'Visa pris',
      Select: 'Välj',
      '2 class': '2 klass',
      '1 class': '1 klass',
      Selected: 'Vald',
      'Travel with a': 'Res med',
      'Night train': 'Nattåg',
      Direct: 'Direkt',
      'Change train': 'Byt tåg',
      'Arrive in': 'Framme i',
      'Run by: %s': 'Körs av: %s',
      'Service: %s': 'Tjänst: %s',
      'Towards: %s': 'Mot: %s',
      Unavailable: 'Ej tillgänglig',
      'Train number: %s': 'Tågnummer: %s',
      'Price fr.': 'Pris fr.',
      'Find tickets': 'Hitta biljetter',
      'Looking for tickets': 'Söker biljetter'
    }
  }

  export const JourneyFragment = gql`
    fragment journey on Journey {
      __typename
      id
      status
      itinerary {
        __typename
        ... on Stopover {
          duration
          location {
            uid
            name
            countryCode
            coordinates {
              type
              coordinates
            }
            image {
              url
              alt
            }
          }
        }
        ... on SegmentCollection {
          status
          segments {
            __typename
            duration
            departureAt
            arrivalAt
            transport
            identifier
            direction
            operator {
              name
              image {
                url
              }
            }
            serviceBrand {
              name
              image {
                url
              }
            }
            origin {
              name
              ... on Location {
                uid
                countryCode
              }
            }
            destination {
              name
              ... on Location {
                uid
                countryCode
              }
            }
          }
        }
      }
    }
  `

  export const JourneyWithAvailabilityFragment = gql`
    fragment journeyWithAvailability on Journey {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          availability {
            status
            ticketClasses {
              serviceClass
            }
            priceFrom {
              amount
              currency
            }
          }
        }
      }
    }
  `

  export const JourneyOfferFragment = gql`
    fragment journeyOffer on JourneyOffer {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          offers {
            parts {
              __typename
              ... on AdmissionPart {
                serviceClass
              }
            }
            price {
              amount
              currency
            }
          }
        }
      }
    }
  `

  /** @param {Schema.JourneyOffer?} [journey] */
  export function getCheapestPrice(
    journey,
    serviceClasses = ['BASIC', 'STANDARD', 'HIGH', 'BEST', 'UNKNOWN']
  ) {
    if (journey?.status !== 'SUCCESS' || !journey.itinerary) return null

    const offers = journey.itinerary
      .filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) =>
        item.offers?.filter((offer) =>
          offer.parts.every(
            (part) =>
              part.__typename !== 'AdmissionPart' ||
              serviceClasses.includes(/** @type {string} */ (part.serviceClass))
          )
        )
      )
      .reduce((acc, offers) => {
        if (!acc || !offers?.length) return undefined

        acc.push(
          offers.reduce((cheapest, offer) => {
            return offer.price.amount < cheapest.price.amount ? offer : cheapest
          })
        )

        return acc
      }, /** @type {Schema.Offer[]} */ ([]))

    if (!offers?.length) return null

    return offers.reduce(
      (price, offer) => ({
        ...offer.price,
        amount: offer.price.amount + price.amount
      }),
      /** @type {Schema.Money} */ ({ amount: 0 })
    )
  }
</script>

<script>
  import * as Duration from 'duration-fns'
  import { tick } from 'svelte'
  import {
    differenceInCalendarDays,
    differenceInMinutes,
    addHours
  } from 'date-fns'

  import { format } from '$lib/utils/duration.svelte.js'
  import { register } from '$lib/utils/text.svelte.js'
  import { money } from '$lib/utils/money.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import Tooltip from '$lib/Tooltip.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Image from '$lib/Image.svelte'

  let text = register(i18n)

  /** @type {{
   * journey?: Schema.Journey?,
   * origin?: Schema.Location?,
   * destination?: Schema.Location?,
   * passengers?: Schema.PassengerPlaceholderInput[],
   * isPassholder?: boolean,
   * withOffer?: boolean,
   * expanded?: boolean,
   * selected?: boolean,
   * disabled?: boolean,
   * action?: Svelte.Snippet<[Schema.Journey, App.GraphQLOperationResult<{ journey: Schema.JourneyOffer }>, { selected: boolean }]>,
   * onselect?: function(Schema.JourneyOffer): void
   * }}*/
  let {
    journey = null,
    // origin = null,
    destination = null,
    isPassholder = false,
    selected = false,
    withOffer = false,
    expanded = $bindable(false),
    passengers,
    disabled,
    action,
    onselect
  } = $props()

  const FIRST_CLASS_SERVICES = ['HIGH', 'BEST']
  const SECOND_CLASS_SERVICES = ['BASIC', 'STANDARD']

  const NON_FATAL_STATUSES = [
    'PREBOOK_NOT_POSSIBLE',
    'INDETERMINATE',
    'UNABLE_TO_SELL',
    'NO_TIMETABLE',
    'UNKNOWN'
  ]

  const WEAK_CONTENT_COUNTRIES = ['PL', 'SK', 'HU', 'SI']

  /** @type {App.GraphQLOperation<{ journey: Schema.JourneyOffer }, { id: string, currency: string, passengers: Schema.PassengerPlaceholderInput[], isPassholder: boolean }>} */
  const GetJourneyOffer = gql`
    subscription GetJourneyOffer(
      $id: ID!
      $currency: String
      $passengers: [PassengerPlaceholderInput!]!
      $isPassholder: Boolean = false
    ) {
      journey: getJourneyOffer(
        journey: $id
        currency: $currency
        passengers: $passengers
        isPassholder: $isPassholder
      ) {
        ...${JourneyOfferFragment}
      }
    }
  `

  let getOffer = $state(withOffer)

  // Since the entire journey object may change we need to pluck properties that are tracked
  let journeyId = $derived(journey?.id)
  let hasCompleteItinerary = $derived(
    journey?.itinerary?.every(
      (item) => item.__typename !== 'SegmentCollection' || item.segments
    )
  )

  $effect(() => {
    if (journeyId && hasCompleteItinerary && getOffer && passengers) {
      return GetJourneyOffer.execute({
        currency: State.currency,
        id: journeyId,
        passengers,
        isPassholder
      })
    }
  })

  let itinerary = $derived(
    GetJourneyOffer.data?.journey?.itinerary ||
      journey?.itinerary?.map((item) => {
        if (item.__typename !== 'SegmentCollection') return item
        return { ...item, status: getOffer ? 'LOADING' : item.status }
      })
  )

  let isSoldOut = $derived(
    journey?.itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) => item.availability)
      .some((availability) => availability?.status === 'SOLD_OUT')
  )

  let priceFrom = $derived(
    journey?.itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .reduce((acc, item, index) => {
        if (!acc && index) return null

        const { availability } = /** @type {Schema.SegmentCollection} */ (item)
        if (!availability?.priceFrom) return null

        const amount = acc ? acc.amount : 0
        return {
          ...availability.priceFrom,
          amount: availability.priceFrom.amount + amount
        }
      }, /** @type {Schema.Money?} */ (null))
  )

  let content
  const toggle = async (event) => {
    event.preventDefault()

    const before = content.getBoundingClientRect()
    expanded = !expanded
    await tick()
    const after = content.getBoundingClientRect()

    content.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    })

    content.animate(
      [{ height: `${before.height}px` }, { height: `${after.height}px` }],
      {
        duration: 400,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    )
  }

  const asTimestamp = $derived.by(() => (date) => {
    return new Date(date).toLocaleTimeString(State.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
  })

  /** @type {function(string, string): boolean} */
  const isSameDay = (a, b) => {
    if (!a || !b) return false
    const [aDate] = a.split('T')
    const [bDate] = b.split('T')
    return aDate === bDate
  }

  /** @param {string} date */
  const isFarAhead = (date) => {
    const threeMonths = Date.now() + 1000 * 60 * 60 * 24 * 90
    return Date.parse(date) > +threeMonths
  }

  /** @param {Schema.SegmentCollection} item */
  const isWeakContentCountry = (item) => {
    const locations = item.segments?.flatMap((segment) => [
      segment.origin,
      segment.destination
    ])
    return locations?.some(
      (location) =>
        'countryCode' in location &&
        WEAK_CONTENT_COUNTRIES.includes(location.countryCode)
    )
  }

  /** @param {Schema.SegmentCollection} item */
  const getStatusMessage = (item) => {
    const { status, segments, availability } = item
    const origin = segments?.[0]?.origin.name
    const destination = segments?.[segments.length - 1]?.destination.name

    switch (status) {
      case 'ONE_DAY':
        return text`It is too close to your selected departure date, try a later date.`
      case 'NO_TIMETABLE':
      case 'OUTSIDE_TIMETABLE':
        return text`The timetable for this route is not yet available. Please try again at a later stage.`
      case 'NO_FARES': {
        if (segments) {
          if (isFarAhead(segments[0].departureAt)) {
            return text`Tickets for your journey are not yet available. Please try again at a later stage.`
          }
          if (isWeakContentCountry(item)) {
            return text`We're currently unable to offer a ticket for this route. Please check with the operator directly.`
          }
        }
        return text`Sorry, our booking system didn't return any results for this route.`
      }
      case 'PREBOOK_NOT_POSSIBLE': {
        if (segments) {
          return text`Tickets between ${origin} and ${destination} are typically purchased at the station.`
        }
        return text`These tickets are typically purchased at the station.`
      }
      case 'INDETERMINATE':
        return null
      case 'UNKNOWN':
      case 'UNABLE_TO_SELL':
      default:
        if (availability?.status === 'SOLD_OUT' && segments) {
          if (segments?.length > 1) {
            return text`Tickets for the journey between ${origin} and ${destination} are sold out.`
          } else {
            return text`Tickets for the ${asTimestamp(segments[0].departureAt)} departure from ${origin} to ${destination} are sold out.`
          }
        }
        if (isWeakContentCountry(item)) {
          return text`We're currently unable to offer a ticket for this route. Please check with the operator directly.`
        }
        return text`There was a problem connecting to the booking system for this route. Please try again later.`
    }
  }

  /** @param {Schema.SegmentCollection[]} items */
  const getSummaryMessage = (items) => {
    if (items.some((item) => item.availability?.status === 'SOLD_OUT')) {
      return text`Tickets are sold out`
    }

    const [status, ...rest] = Array.from(
      new Set(items.map((item) => item.status))
    )
    if (!rest.length) {
      switch (status) {
        case 'PREBOOK_NOT_POSSIBLE':
          return text`Prebook not possible`
        case 'NO_TIMETABLE':
        case 'OUTSIDE_TIMETABLE':
          return text`It's too early to book`
        case 'NO_FARES': {
          if (items[0].segments) {
            if (isFarAhead(items[0].segments[0].departureAt)) {
              return text`It's too early to book`
            }
          }
          if (isWeakContentCountry(items[0])) {
            return text`Check with operator`
          }
          return text`No tickets found`
        }
        case 'UNKNOWN':
          if (isWeakContentCountry(items[0])) {
            return text`Check with operator`
          }
          return text`Tickets unavailable`
      }
    }

    return text`No tickets found`
  }

  /** @param {Schema.SegmentCollection[]} items */
  const getMessageSeverity = (items) => {
    const severities = items.map((item) => {
      if (item.availability?.status === 'SOLD_OUT') return 1
      if (NON_FATAL_STATUSES.includes(item.status)) return 0
      if (item.status === 'NO_FARES') {
        const departureAt = item.segments?.[0].departureAt
        if (departureAt && isFarAhead(departureAt)) return 0
        if (isWeakContentCountry(item)) return 0
      }
      return 1
    })

    const severity = Math.min(...severities)
    return ['warning', 'error'][severity]
  }

  const getStatusSymbol = (status) => {
    switch (status) {
      case 'LOADING':
        return 'spinner'
      case 'SUCCESS':
        return 'checkmark'
      default:
        return 'question'
    }
  }

  /** @param {Schema.Stopover} stopover */
  const formatStopoverDuration = (stopover) => {
    if (!itinerary) return null

    const index = itinerary.indexOf(stopover)
    const { nights } = itinerary
      .slice(0, index + 1)
      .reduce((acc, item, index) => {
        // Keep track of the arrival time of the previous segment
        if (item.__typename === 'SegmentCollection') {
          const lastSegment = item.segments?.[item.segments.length - 1]
          return { nights: 0, date: new Date(lastSegment?.arrivalAt) }
        }

        // Only count stopovers
        if (item.__typename !== 'Stopover') {
          return { nights: 0, date: null }
        }

        const duration = Duration.parse(item.duration)
        if (acc.date) {
          const hours = Duration.toHours(duration)
          const date = addHours(acc.date, hours)
          const diff = differenceInCalendarDays(date, acc.date)
          return { nights: diff, date }
        }

        return { nights: 0, date: null }
      }, /** @type {{ nights: number, date: Date|null }} */ ({ nights: 0, date: null }))

    return nights ? text`${nights} nights in` : text`Passing through`
  }

  /** @param {Schema.Segment} segment */
  const isNightTrain = (segment) => {
    return segment.transport === 'NIGHT_TRAIN' && segment.duration > 300
  }
</script>

{#snippet header(/** @type {Schema.Journey?} */ journey)}
  {@const segments = journey?.itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
    .filter(Boolean)}
  <div class="header">
    {#if !segments?.every(Boolean)}
      <span class="time u-loading" style:width="12ch">&nbsp;</span>
    {:else if segments.length}
      <span class="time">
        <time datetime={segments.at(0).departureAt}>
          {asTimestamp(segments.at(0).departureAt)}
        </time>
        <span class="divider">–</span>
        <time datetime={segments.at(-1).arrivalAt}>
          {asTimestamp(segments.at(-1).arrivalAt)}
        </time>
      </span>
    {/if}
    {#if journey}
      <button class="toggle" type="button" onclick={toggle}>
        <span class="chevron">
          <Symbol name="chevron" size="sm" />
        </span>
        {expanded ? text`Hide details` : text`Show details`}
      </button>
    {/if}
  </div>
{/snippet}

{#snippet summary(/** @type {Schema.Journey?} */ journey)}
  {@const highlights = itinerary?.filter(
    (item) =>
      item.__typename === 'Stopover' ||
      (item.__typename === 'SegmentCollection' &&
        item.segments?.some(isNightTrain))
  )}
  {@const stopovers = highlights?.filter(
    (item) => item.__typename === 'Stopover'
  )}
  {@const segments = itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
    .filter(Boolean)}
  {@const isLoading = !segments?.every(Boolean)}
  {@const operators = segments?.reduce((operators, segment) => {
    if (
      operators.every((operator) => operator.name !== segment.operator.name)
    ) {
      operators.push(segment.operator)
    }
    return operators
  }, /** @type {Schema.Operator[]} */ ([]))}
  {#if itinerary?.length && highlights?.length}
    <ol class="highlights">
      {#each highlights as item}
        <li class="highlight">
          <div
            class="thumbnail"
            class:dark={item.__typename === 'SegmentCollection'}>
            {#if item.__typename === 'Stopover'}
              {#if item.location.image}
                <Image
                  src={item.location.image.url}
                  alt={item.location.image.alt || item.location.name}
                  aspect={1}
                  transforms="q_50"
                  variants={[76]}
                  class="img" />
              {:else}
                <Symbol name="pin" />
              {/if}
            {/if}
            {#if item.__typename === 'SegmentCollection'}
              <svg
                class="img"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 23 19">
                <circle cx="20.5" cy="9.5" r=".5" fill="#FC3" />
                <circle cx=".5" cy="1.5" r=".5" fill="#FC3" />
                <circle cx="22.5" cy="5.5" r=".5" fill="#FC3" />
                <circle cx="14.5" cy=".5" r=".5" fill="#FC3" />
                <circle cx="8.5" cy="3.5" r=".5" fill="#FC3" />
                <circle cx="17" cy="5" r="1" fill="#FC3" />
                <circle cx="1.5" cy="6.5" r=".5" fill="#FC3" />
                <path
                  fill="#FC3"
                  d="M6 12.1c0 3.4 2.8 6.2 6.2 6.2 2.2 0 3-.5 4.5-1.9.5-.5.6-1 0-1-1 0-3.7-.6-5-2.8-1.3-2.2-.2-5.1.3-6.1 0-.3 0-.5-.3-.5A6.1 6.1 0 0 0 6 12.1Z" />
              </svg>
            {/if}
          </div>
          <div class="truncate">
            {#if item.__typename === 'Stopover'}
              {formatStopoverDuration(item)}
              <br />
              <strong>{item.location.name}</strong>
            {/if}
            {#if item.__typename === 'SegmentCollection'}
              {text`Travel with a`}
              <br />
              <strong>{text`Night train`}</strong>
            {/if}
          </div>
        </li>
      {/each}
    </ol>
  {/if}
  <div class="details">
    <ol class="pills">
      <li class="pill" class:u-loading={isLoading}>
        {#if isLoading}
          <div style:width="5ch">&nbsp;</div>
        {:else}
          {@const duration = journey?.itinerary?.reduce((acc, item) => {
            if (item.__typename === 'Stopover') {
              return acc + Duration.toMinutes(Duration.parse(item.duration))
            }
            if (item.__typename === 'SegmentCollection' && item.segments) {
              return item.segments?.reduce((acc, segment) => {
                return acc + segment.duration
              }, acc)
            }
            return acc
          }, 0)}
          {#if duration}
            {format({ minutes: duration })}
          {/if}
        {/if}
      </li>
      <li class="pill" class:u-loading={isLoading}>
        {#if isLoading}
          <div style:width="7ch">&nbsp;</div>
        {:else if segments && stopovers}
          {segments.length > 1
            ? text`${segments.length - 1} changes`
            : text`Direct`}
        {/if}
      </li>
    </ol>
    {#if operators?.length}
      {@const withImage = operators.filter((item) => item.image).slice(0, 2)}
      <ol class="operators">
        {#each withImage as operator}
          {#if operator.image}
            <li class="operator">
              <img
                src={operator.image.url}
                alt={operator.name}
                class="logotype" />
            </li>
          {/if}
        {/each}
        {#if operators.length > withImage.length}
          <li class="operator">
            +{operators.length - withImage.length}
          </li>
        {/if}
      </ol>
    {/if}
  </div>
{/snippet}

{#snippet highlight(location, label)}
  <div class="highlight frame">
    <div class="thumbnail">
      {#if location.image}
        <Image
          src={location.image.url}
          alt={location.image.alt || location.name}
          aspect={1}
          transforms="q_50"
          variants={[76]}
          class="img" />
      {:else}
        <Symbol name="pin" />
      {/if}
    </div>
    <div class="truncate">
      {label}
      <br />
      <strong>{location.name}</strong>
    </div>
  </div>
{/snippet}

{#snippet day({
  /** @type {number} */ index,
  /** @type {Schema.Segment[]} */ segments,
  /** @type {Schema.ItineraryItem?} */ prevItem
})}
  {@const segment = segments[index]}
  {@const prevSegment =
    index === 0
      ? prevItem?.__typename === 'SegmentCollection'
        ? prevItem.segments?.at(-1)
        : null
      : segments[index - 1]}
  {#if (!prevItem && !index) || prevItem?.__typename === 'Stopover' || !isSameDay(prevSegment?.departureAt, segment.departureAt)}
    <time class="day" datetime={segment.departureAt}>
      {new Date(segment.departureAt).toLocaleDateString(State.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      })}
    </time>
  {:else if prevSegment}
    {@const duration = differenceInMinutes(
      new Date(segment.departureAt),
      new Date(prevSegment.arrivalAt)
    )}
    <span class="change">
      {text`Change train`} ({format({ minutes: duration })})
    </span>
  {/if}
{/snippet}

{#snippet leg({
  /** @type {Schema.Segment} */ segment,
  /** @type {Schema.SegmentCollectionStatus} */ status
})}
  <div class="segment frame" class:nighttrain={isNightTrain(segment)}>
    {#if getOffer}
      {@const statusClass = status
        .replace(/^(?!SUCCESS$|LOADING$).*/, 'error')
        .toLowerCase()}
      <span class="status {statusClass}">
        <Symbol name={getStatusSymbol(status)} size="md" circle />
      </span>
    {/if}
    <ol>
      <li class="row">
        <time datetime={segment.departureAt} class="time">
          {asTimestamp(segment.departureAt)}
        </time>
        {segment.origin.name}
      </li>
      <li class="row">
        <span class="duration">
          {format({ minutes: segment.duration })}
        </span>
        <ul class="train">
          <li class="info">
            <Tooltip
              text={text`Train number: ${segment.identifier?.replace(/^(\d)/, '#$1')}`}>
              {segment.identifier?.replace(/^(\d)/, '#$1')}
            </Tooltip>
          </li>
          <li class="info">
            {#if segment.operator.image}
              <Tooltip text={text`Run by: ${segment.operator.name}`}>
                <img
                  class="logotype"
                  src={segment.operator.image.url}
                  alt={segment.operator.name} />
              </Tooltip>
            {:else}
              {segment.operator.name}
            {/if}
          </li>
          {#if segment.serviceBrand}
            <li class="info">
              {#if segment.serviceBrand.image}
                <Tooltip text={text`Service: ${segment.serviceBrand.name}`}>
                  <img
                    class="logotype"
                    src={segment.serviceBrand.image.url}
                    alt={segment.serviceBrand.name} />
                </Tooltip>
              {:else}
                {segment.serviceBrand.name}
              {/if}
            </li>
          {/if}
          {#if segment.direction}
            <li class="info shrink">
              <Tooltip text={text`Towards: ${segment.direction}`}>
                <span class="direction">
                  <Symbol name="arrow" size="xs" />
                  {segment.direction}
                </span>
              </Tooltip>
            </li>
          {/if}
        </ul>
      </li>
      <li class="row">
        <time datetime={segment.arrivalAt} class="time">
          {asTimestamp(segment.arrivalAt)}
        </time>
        {segment.destination.name}
      </li>
    </ol>
  </div>
{/snippet}

{#snippet sidebar(
  /** @type {Schema.Journey?} */ journey,
  /** @type {Schema.JourneyOffer|undefined} */ journeyOffer
)}
  {@const secondClassPrice = getCheapestPrice(
    journeyOffer,
    SECOND_CLASS_SERVICES
  )}
  {@const firstClassPrice = getCheapestPrice(
    journeyOffer,
    FIRST_CLASS_SERVICES
  )}
  {@const unknownClassPrice = getCheapestPrice(journeyOffer, ['UNKNOWN'])}
  {@const ticketClasses = journey?.itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    ?.flatMap(
      (/** @type {Schema.SegmentCollection} */ item) =>
        item.availability?.ticketClasses
    )}
  {@const secondClassAvailability =
    Boolean(secondClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || SECOND_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const firstClassAvailability =
    Boolean(firstClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || FIRST_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const unavailable = isSoldOut || journeyOffer?.status === 'ERROR'}
  {@const isLoading = getOffer
    ? !journeyOffer || journeyOffer?.status === 'LOADING'
    : journey?.status === 'LOADING'}

  {#snippet classPrice(price)}
    {#if unavailable}
      <span class="label muted">{text`Unavailable`}</span>
    {:else if isLoading}
      <div class="dots">
        <Symbol name="dots" size="fill" />
      </div>
    {:else if price}
      <span class="price">{money(price)}</span>
    {/if}
  {/snippet}

  {#snippet uncertainClassPrice(price)}
    <div class="shared">
      <span class="label">{text`Price fr.`}</span>
      <span class="price">{money(price)}</span>
    </div>
  {/snippet}

  <div class="sidebar" class:unavailable>
    <div class="overview">
      <div class="classes">
        <div class="class">
          <span
            class="label"
            class:muted={!secondClassPrice && !secondClassAvailability}>
            {text`2 class`}
          </span>
          {@render classPrice(secondClassPrice)}
        </div>
        <div class="class">
          <span
            class="label"
            class:muted={!firstClassPrice && !firstClassAvailability}>
            {text`1 class`}
          </span>
          {@render classPrice(firstClassPrice)}
        </div>
      </div>
      {#if priceFrom && !getOffer}
        {@render uncertainClassPrice(priceFrom)}
      {/if}
      {#if !secondClassPrice && !firstClassPrice && unknownClassPrice}
        {@render uncertainClassPrice(unknownClassPrice)}
      {/if}
    </div>
    {#if unavailable}
      {#if itinerary}
        {@const errored = itinerary.filter(
          (item) =>
            item.__typename === 'SegmentCollection' &&
            ((item.status !== 'SUCCESS' && item.status !== 'LOADING') ||
              item.availability?.status === 'SOLD_OUT')
        )}
        {#if expanded}
          {#each errored as item}
            <p
              class="message {getMessageSeverity(
                /** @type {Schema.SegmentCollection[]} */ (errored)
              )}">
              {getStatusMessage(/** @type {Schema.SegmentCollection} */ (item))}
            </p>
          {/each}
        {:else}
          <p
            class="message summary {getMessageSeverity(
              /** @type {Schema.SegmentCollection[]} */ (errored)
            )}">
            {getSummaryMessage(
              /** @type {Schema.SegmentCollection[]} */ (errored)
            )}
            <button type="button" class="u-link expand" onclick={toggle}>
              {text`Show details`}
            </button>
          </p>
        {/if}
      {/if}
    {:else if journey && hasCompleteItinerary}
      <button
        type="submit"
        class="action"
        class:primary={journeyOffer?.status === 'SUCCESS'}
        disabled={Boolean(
          disabled ||
            GetJourneyOffer.error ||
            GetJourneyOffer.fetching ||
            journeyOffer?.status === 'ERROR'
        )}
        name={journeyOffer ? 'journeyOfferId' : 'journeyId'}
        value={journeyOffer?.id || journey.id}
        onclick={() => {
          if (!getOffer) {
            getOffer = true
          } else if (
            journeyOffer?.status === 'SUCCESS' &&
            typeof onselect === 'function'
          ) {
            onselect(journeyOffer)
          }
        }}>
        {#if action}
          {@render action(journey, GetJourneyOffer, { selected })}
        {:else if selected}
          {text`Selected`}
        {:else if journeyOffer?.status === 'SUCCESS'}
          {text`Select`}
        {:else if GetJourneyOffer.fetching}
          {text`Looking for tickets`}
        {:else}
          {text`Find tickets`}
        {/if}
      </button>
    {/if}
  </div>
{/snippet}

<article
  class="journey"
  class:selected
  class:expanded
  data-journey={journey?.id}>
  <div class="container">
    <div bind:this={content} class="content">
      {@render header(GetJourneyOffer.data?.journey || journey)}
      {#if expanded && itinerary}
        <div class="items">
          {#each itinerary as item, index (`${item.__typename}-${index}`)}
            {@const prev = itinerary[index - 1]}
            {#if item.__typename === 'Stopover'}
              <div class="item">
                {@render highlight(item.location, formatStopoverDuration(item))}
              </div>
            {:else if item.__typename === 'SegmentCollection' && item.segments}
              {@const segments = item.segments}
              <div class="item">
                {@render day({ index: 0, segments, prevItem: prev })}
                <div class="legs">
                  {#each segments as segment, index}
                    {#if index}
                      {@render day({ index, segments, prevItem: prev })}
                    {/if}
                    {@render leg({ segment, status: item.status })}
                  {/each}
                  {#if (item.status !== 'SUCCESS' && item.status !== 'LOADING') || item.availability?.status === 'SOLD_OUT'}
                    <p class="message anchored {getMessageSeverity([item])}">
                      {getStatusMessage(item)}
                    </p>
                  {/if}
                </div>
              </div>
            {/if}
          {/each}
          {#if destination}
            <div class="item">
              {@render highlight(destination, text`Arrive in`)}
            </div>
          {/if}
        </div>
      {:else}
        {@render summary(GetJourneyOffer.data?.journey || journey)}
      {/if}
    </div>
    {@render sidebar(journey, GetJourneyOffer.data?.journey)}
  </div>
</article>

<style>
  .journey {
    container-type: inline-size;
    position: relative;
    color: rgb(var(--rgb-text));
    transition: var(--box-shadow-transition);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
    font-size: 1rem;
  }

  .journey.selected::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    pointer-events: none;
    border: 3px solid rgb(var(--rgb-primary, var(--rgb-bright-green)));
    border-radius: var(--border-radius-lg);
  }

  .container {
    display: flex;
    flex-direction: column;
  }

  @container (width >= 35rem) {
    .container {
      display: flex;
      flex-direction: row;
    }
  }

  .content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem 1rem 1rem;
    position: relative;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    background: var(--background);
  }

  .journey.expanded .content {
    gap: 0.5rem;
  }

  @container (width >= 35rem) {
    .content {
      border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
    }
  }

  .sidebar {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    gap: 1.75rem;
    padding: 1rem 1rem 1rem 1.5rem;
    position: relative;
    background: var(--background);
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    font-size: 0.9375rem;
    border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  }

  @container (width >= 35rem) {
    .sidebar {
      flex: 0 0 14rem;
      border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
      border-top: 0;
      border-left: 1px solid rgba(var(--rgb-muted), 0.2);
    }
  }

  .sidebar.unavailable {
    display: grid;
    gap: 1.5rem;
    padding: 1rem;
    font-size: 0.875rem;
  }

  .overview {
    width: 100%;
    display: flex;
    align-items: baseline;
  }

  .classes {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  .class {
    display: flex;
    justify-content: space-between;
    height: 1.25rem;
  }

  .class .label {
    padding-top: 0.35rem;
  }

  .class + .class {
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    padding-top: 0.375rem;
  }

  .shared {
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
    gap: 0.1875rem;
    padding-left: 2rem;
  }

  .label {
    font-weight: 500;
    font-size: 0.8125rem;
    line-height: 1;
    white-space: nowrap;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .label.muted {
    color: rgba(var(--rgb-muted), 0.6);
  }

  .classes:has(.muted) .label:not(.muted) {
    font-weight: 600;
  }

  .dots {
    width: 1.4rem;
    height: 1.5rem;
  }

  .price {
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1;
    text-align: right;
    letter-spacing: -0.04em;
  }

  /** Header */

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(var(--rgb-gray), 0.2);
  }

  .time {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: bold;
    line-height: 1;
  }

  .divider {
    font-weight: 400;
    margin: 0 0.25rem;
  }

  .toggle {
    display: flex;
    align-items: center;
    cursor: default;
    gap: 0.15rem;
    user-select: none;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
    position: relative;
    top: 0.125rem;
  }

  @media (hover) {
    .toggle {
      opacity: 0;
      transition: opacity 100ms var(--ease-in);
    }
  }

  .journey:hover .toggle,
  .journey.expanded .toggle {
    opacity: 1;
    transition: opacity 150ms var(--ease-out);
  }

  .journey:not(.expanded) .toggle::before {
    content: '';
    width: 100%;
    height: 100;
    position: absolute;
    inset: 0;
  }

  @media (scripting: none) {
    .toggle {
      display: none;
    }
  }

  .chevron {
    transform-origin: center;
  }

  .journey.expanded .chevron {
    transform: rotate(180deg);
  }

  /** Highlights */

  .highlights {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    white-space: nowrap;
  }

  .highlight {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    line-height: 1.15;
  }

  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /** Thumbnail */

  .thumbnail {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 2.375rem;
    height: 2.375rem;
    overflow: hidden;
    border-radius: var(--border-radius-xs);
    background-color: var(--background-alt);
  }

  .thumbnail.dark {
    background: rgba(var(--rgb-blue));
  }

  .thumbnail :global(.img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  /** Details */

  .details {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /** Pills */

  .pills {
    display: flex;
    gap: 0.5rem;
  }

  .pill {
    padding: 0.25rem 0.75rem 0.2rem;
    white-space: nowrap;
    background: var(--background-alt);
    border-radius: var(--border-radius-full);
    color: rgb(var(--tgb-text));
    font-weight: 600;
    font-size: 0.875rem;
  }

  .operators {
    display: flex;
    gap: 0.85rem;
    align-items: center;
    margin-bottom: 0.55rem;
  }

  @container (width < 25rem) {
    .operators {
      display: none;
    }
  }

  .operator {
    font-weight: bold;
    font-size: 0.875rem;
  }

  .logotype {
    flex: 0 0 auto;
    max-height: 1.25rem;
    width: auto;
  }

  .direction {
    display: inline-flex;
    align-items: center;
    font-size: 0.875rem;
    font-weight: normal;
    color: rgb(var(--rgb-muted));
  }

  /** Actions */

  .option {
    display: contents;
  }

  .availability {
    color: rgb(var(--rgb-muted));
  }

  .action {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 2.5rem;
    width: 100%;
    padding: 0.6rem 1rem;
    border: transparent;
    background: transparent;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    cursor: pointer;
    user-select: none;
    font-weight: 600;
    font-size: 1rem;
    border-radius: var(--border-radius-full);
    transition: var(--box-shadow-transition);
    box-shadow: var(--box-shadow-sm);
  }

  .action:not(:disabled):hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .action:not(:disabled):active {
    box-shadow: var(--box-shadow-sm-active);
    transition: var(--box-shadow-active-transition);
  }

  .action.primary {
    background: rgb(var(--rgb-primary, var(--rgb-green)));
    color: rgb(var(--rgb-white));
    font-weight: 600;
  }

  .journey.selected .action {
    background: rgb(var(--rgb-primary, var(--rgb-green)));
    color: rgb(var(--rgb-white));
  }

  :global(.using-keyboard) .journey.selected .action {
    outline: solid var(--current-focus-ring-width, var(--focus-ring-width));
    outline-color: rgb(
      var(--current-focus-ring-color, var(--focus-ring-color))
    );
  }

  .action:disabled {
    background: transparent;
    color: rgba(var(--rgb-muted), 0.6);
    border: 1px solid rgba(var(--rgb-muted), 0.25);
    cursor: default;
    font-weight: 400;
    box-shadow: none;
  }

  /** Loader */

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    font-size: 0.75rem;
    font-weight: bold;
    text-align: center;
    white-space: nowrap;
    color: rgb(var(--rgb-muted));
  }

  @container (width >= 35rem) {
    .loader {
      flex-direction: column;
    }
  }

  /** Items */

  .items,
  .item {
    display: grid;
    gap: 0.5rem;
  }

  /** Frame */

  .frame {
    position: relative;
    padding: 0.75rem;
    border: 1px solid rgba(var(--rgb-muted), 0.25);
    border-radius: var(--border-radius-sm);
  }

  /** Legs */

  .legs {
    display: grid;
    gap: 0.5rem;
    position: relative;
  }

  .message {
    padding: 0.25rem 1rem 0.25rem 1rem;
    position: relative;
  }

  .message.anchored {
    display: none;
  }

  .message::before {
    content: '';
    width: 0.2rem;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: rgb(var(--rgb-red));
    border-radius: var(--border-radius-full);
  }

  .message.warning::before {
    background-color: rgb(var(--rgb-yellow));
  }

  @container (width >= 35rem) {
    .message:not(.anchored) {
      display: none;
    }

    .journey:not(.expanded) .message.summary {
      display: block;
    }

    .message.anchored {
      display: block;
      width: 14rem;
      height: 100%;
      padding: 0.25rem 1rem 0.5rem 1.75rem;
      position: absolute;
      top: 0;
      left: calc(100% + 1.5rem);
      z-index: 1;
      font-size: 0.875rem;
      color: rgb(var(--text-color));
    }

    .message.anchored::before {
      left: 1rem;
    }
  }

  .expand {
    --rgb-link: var(--rgb-muted);

    display: block;
    margin-top: 0.5rem;
  }

  /** Segment */

  .segment {
    font-weight: 600;
    white-space: nowrap;
  }

  .day {
    display: block;
    font-weight: 600;
    margin: 0.5rem 0;
  }

  .change {
    display: block;
    font-size: 0.875rem;
  }

  .status {
    --current-color: rgb(var(--rgb-text));
    --current-background: rgba(var(--rgb-muted), 0.2);

    display: block;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
    white-space: normal;
  }

  .status.error {
    --current-color: rgba(var(--rgb-muted));
    --current-background: rgba(var(--rgb-light-gray), 0.4);
  }

  .status.success {
    --current-color: rgb(var(--rgb-green));
    --current-background: rgb(var(--rgb-bright-green), 0.2);
  }

  .row {
    display: grid;
    grid-template-columns: 5rem auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0.2rem 0;
    position: relative;
  }

  .row::before {
    content: '';
    width: 1.5rem;
    height: 100%;
    position: absolute;
    left: 3.5rem;
    top: 0;
    background-color: rgb(var(--rgb-primary, var(--rgb-highlight)));
  }

  .segment.nighttrain .row::before {
    background-color: rgb(var(--rgb-blue));
  }

  .row:first-child::before {
    border-radius: var(--border-radius-full) var(--border-radius-full) 0 0;
  }

  .row:last-child::before {
    border-radius: 0 0 var(--border-radius-full) var(--border-radius-full);
  }

  .segment .time {
    display: block;
    padding-right: 2.25rem;
    position: relative;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .segment .time::after {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    position: absolute;
    top: 50%;
    right: 0.75rem;
    border-radius: 100%;
    background: var(--background);
    transform: translate(50%, -50%);
  }

  .duration {
    padding-right: 2.25rem;
    font-weight: normal;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  .train {
    display: flex;
    align-items: center;
    gap: 0.25rem 0.5rem;
    flex-wrap: wrap;
    white-space: nowrap;
    font-weight: normal;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  .info {
    flex: 0 0 auto;
  }

  .info.shrink {
    flex-shrink: 1;
  }

  .train .logotype {
    height: 1.1rem;
  }
</style>
